import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import {
  AXIS_Y, BOOSTER_STAGES, ENGINE, EngineModule, FAN_BLADES, HPC_STAGES, HPT_STAGES, LPT_STAGES,
  Part, VIEW_H, VIEW_W,
} from './engineGeometry';

/*
 * 2D fallback for the startup loader (reduced motion, no WebGL2, weak GPU):
 * an engineering blueprint half-section that draws itself, explodes into its
 * modules along the shaft, gets a walk-round scan, and closes up again.
 *
 * Motion model: timers only switch `data-phase`; CSS transitions do all the
 * movement (see startupLoader.css). Transitions, unlike keyframes, can be
 * redirected mid-flight, so when `finishing` arrives mid-explode the engine
 * closes up from wherever it is.
 */

interface Props {
  reduced: boolean;
  /** Data is in: close up, then onSettled (the shell fades out). */
  finishing: boolean;
  onSettled: () => void;
}

type Phase = 'pre' | 'draw' | 'hold' | 'explode' | 'inspect' | 'collapse' | 'finish';
type DrawState = 'pending' | 'running' | 'fast' | 'done';

const DUR = {
  draw: 2300,
  fade: 450,
  hold: 1000,
  // Move duration (1.8s) plus the per-module stagger, so each move lands.
  explode: 2100,
  inspect: 2500,
  collapse: 2100,
  // 0.9s close-up + stagger; the last module lands under the shell's fade.
  finish: 1000,
  reducedFinish: 500,
};

const SESSION_KEY = 'ds-startup-drawn';

const readDrawn = () => {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const markDrawn = () => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    /* storage unavailable - the draw-in simply plays again next time */
  }
};

const MIRROR = `matrix(1 0 0 -1 0 ${AXIS_Y * 2})`;
const VIEW_BOX = `0 0 ${VIEW_W} ${VIEW_H}`;

const Paths = ({ parts, drawable = true }: { parts: Part[]; drawable?: boolean }) => (
  <>
    {parts.map(p => (
      <path key={p.ink} d={p.d} className={`sl-${p.ink}`} pathLength={drawable ? 1 : undefined} />
    ))}
  </>
);

const Layer = ({ className, style, children }: { className: string; style?: CSSProperties; children: React.ReactNode }) => (
  <svg className={`sl-layer ${className}`} style={style} viewBox={VIEW_BOX} preserveAspectRatio="xMidYMid meet" focusable="false">
    {children}
  </svg>
);

const ModuleCallout = ({ m }: { m: EngineModule }) => {
  const c = m.callout;
  if (!c) return null;
  return (
    <g className="sl-callout">
      <path className="sl-leader" d={`M${c.bx} ${c.by + 10}L${c.ax} ${c.ay}`} />
      <circle className="sl-anchor" cx={c.ax} cy={c.ay} r={1.8} />
      <circle className="sl-balloon" cx={c.bx} cy={c.by} r={10} />
      <text className="sl-t sl-callout-num" x={c.bx} y={c.by + 3.5}>{c.n}</text>
      <text className="sl-t sl-callout-name sl-detail" x={c.bx} y={c.by - 17}>{c.name}</text>
    </g>
  );
};

const ModuleLayers = ({ m }: { m: EngineModule }) => {
  const vars = (sgn: number) =>
    ({ '--i': m.i, '--o': m.order, '--dy': `${((m.dy / VIEW_H) * 100).toFixed(3)}%`, '--sgn': sgn } as CSSProperties);
  const geom = <use href={`#sl-g-${m.id}`} />;
  const topOnly = m.topOnly.length > 0 && <use href={`#sl-g-${m.id}-top`} />;

  if (!m.dy) {
    return (
      <Layer className="sl-ink sl-mod" style={vars(0)}>
        {geom}
        {topOnly}
        <g transform={MIRROR}>{geom}</g>
        <ModuleCallout m={m} />
      </Layer>
    );
  }
  // Parts that lift radially need a layer per half: the halves move apart.
  return (
    <>
      <Layer className="sl-ink sl-mod" style={vars(-1)}>
        {geom}
        {topOnly}
        <ModuleCallout m={m} />
      </Layer>
      <Layer className="sl-ink sl-mod" style={vars(1)}>
        <g transform={MIRROR}>{geom}</g>
      </Layer>
    </>
  );
};

/** Static blueprint dressing: hatch pattern, geometry defs, centre line, title block. */
const BaseLayer = () => (
  <Layer className="sl-base">
    <defs>
      <pattern id="sl-hatch" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect className="sl-hatch-bg" width="4" height="4" />
        <path className="sl-hatch-line" d="M2 0V4" />
      </pattern>
      {ENGINE.modules.map(m => (
        <React.Fragment key={m.id}>
          <g id={`sl-g-${m.id}`}><Paths parts={m.parts} /></g>
          {m.topOnly.length > 0 && <g id={`sl-g-${m.id}-top`}><Paths parts={m.topOnly} /></g>}
        </React.Fragment>
      ))}
      <g id="sl-g-shafts"><Paths parts={ENGINE.shafts} /></g>
    </defs>
    <g className="sl-base-ink">
      <path className="sl-centreline" d={`M40 ${AXIS_Y}H${VIEW_W - 40}`} />
      <g className="sl-detail">
        <path className="sl-frame" d="M898 580H1188V634H898Z M898 598H1188 M898 614H1188 M1010 614V634 M1100 614V634" />
        <text className="sl-t" x={906} y={593}>HIGH-BYPASS TURBOFAN — SECTION A-A</text>
        <text className="sl-t sl-t--sm sl-t--muted" x={906} y={609}>
          {`${FAN_BLADES} FAN BLADES · LPC ${BOOSTER_STAGES} · HPC ${HPC_STAGES} · HPT ${HPT_STAGES} · LPT ${LPT_STAGES}`}
        </text>
        <text className="sl-t sl-t--sm" x={906} y={627}>SCALE 1:20</text>
        <text className="sl-t sl-t--sm" x={1018} y={627}>MT-ENG-001</text>
        <text className="sl-t sl-t--sm sl-t--muted" x={1108} y={627}>SHEET 1/1</text>
      </g>
    </g>
  </Layer>
);

const EngineDrawing = () => (
  <div className="sl-engine" aria-hidden="true">
    <BaseLayer />
    <Layer className="sl-ink sl-shafts" style={{ '--o': 0 } as CSSProperties}>
      <use href="#sl-g-shafts" />
      <g transform={MIRROR}><use href="#sl-g-shafts" /></g>
    </Layer>
    <Layer className="sl-shafts-dashed">
      <path d={ENGINE.shaftOutline} />
      <g transform={MIRROR}><path d={ENGINE.shaftOutline} /></g>
    </Layer>
    {ENGINE.modules.map(m => <ModuleLayers key={m.id} m={m} />)}
    <Layer className="sl-ink sl-dims" style={{ '--o': 8 } as CSSProperties}>
      <Paths parts={ENGINE.dims.lines} />
      {ENGINE.dims.labels.map(l => (
        <text
          key={l.text}
          className="sl-t sl-detail"
          x={l.x}
          y={l.y}
          textAnchor="middle"
          transform={l.rotate ? `rotate(${l.rotate} ${l.x} ${l.y})` : undefined}
        >
          {l.text}
        </text>
      ))}
    </Layer>
    <div className="sl-scan-track"><div className="sl-scan" /></div>
  </div>
);

const Blueprint2D = ({ reduced, finishing, onSettled }: Props) => {
  const [intro] = useState<'draw' | 'fade'>(() => (readDrawn() ? 'fade' : 'draw'));
  const [phase, setPhase] = useState<Phase>(reduced ? 'hold' : 'pre');
  const [draw, setDraw] = useState<DrawState>(reduced ? 'done' : 'pending');
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  // Start after the first paint so the "pending" styles are the transition origin.
  useEffect(() => {
    if (reduced) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        setPhase('draw');
        setDraw('running');
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [reduced]);

  // Phase machine.
  useEffect(() => {
    let ms: number;
    let next: () => void;
    switch (phase) {
      case 'draw':
        ms = intro === 'draw' ? DUR.draw : DUR.fade;
        next = () => {
          markDrawn();
          setDraw('done');
          setPhase('hold');
        };
        break;
      case 'hold':
        if (reduced) return;
        ms = DUR.hold;
        next = () => setPhase('explode');
        break;
      case 'explode':
        ms = DUR.explode;
        next = () => setPhase('inspect');
        break;
      case 'inspect':
        ms = DUR.inspect;
        next = () => setPhase('collapse');
        break;
      case 'collapse':
        ms = DUR.collapse;
        next = () => setPhase('hold');
        break;
      case 'finish':
        ms = reduced ? DUR.reducedFinish : DUR.finish;
        next = () => {
          markDrawn();
          onSettledRef.current();
        };
        break;
      default:
        return;
    }
    const t = window.setTimeout(next, ms);
    return () => window.clearTimeout(t);
  }, [phase, intro, reduced]);

  // Data is in: close up from wherever the engine is right now.
  useEffect(() => {
    if (!finishing) return;
    setPhase('finish');
    setDraw(d => (d === 'pending' || d === 'running' ? 'fast' : d));
  }, [finishing]);

  return (
    <div className="sl-stage" data-phase={phase} data-draw={draw} data-intro={intro}>
      <div className="sl-glow sl-glow--dim" aria-hidden="true" />
      <div className="sl-glow sl-glow--bright" aria-hidden="true" />
      <EngineDrawing />
    </div>
  );
};

export default Blueprint2D;
